// Inequalities and Linear Programming Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const inequalitiesLinearProgrammingSamples = {
  fundamental: [
    {
      id: 'ineq_linprog_fund_01',
      problem: "Solve 2x + 3 > 7",
      problemType: "inequalities_linear_programming",
      difficulty: "fundamental",
      description: "Basic linear inequality",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with linear expression",
            value: "2x + 3 > 7",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "subtraction_step",
            name: "Subtraction Step",
            type: "values",
            description: "Subtract 3 from both sides",
            value: "2x > 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by 2",
            value: "x > 2",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x > 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "inequality_properties",
            name: "Inequality Properties",
            type: "formulas",
            description: "Properties of inequalities: adding/subtracting same number preserves inequality",
            value: "If a > b, then a + c > b + c and a - c > b - c",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_operation",
            name: "Identify Operation",
            type: "steps",
            description: "Recognize the inequality to solve",
            value: "Step 1: 2x + 3 > 7",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "subtract_constant",
            name: "Subtract Constant",
            type: "steps",
            description: "Subtract 3 from both sides",
            value: "Step 2: 2x > 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "divide_coefficient",
            name: "Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by 2",
            value: "Step 3: x > 2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_solution",
            name: "Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 4: x > 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "subtraction_step", type: "simplified by", strength: 10 },
          { from: "subtraction_step", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "equals", strength: 10 },
          { from: "inequality_properties", to: "solution", type: "gives", strength: 10 },
          { from: "identify_operation", to: "subtract_constant", type: "enables", strength: 8 },
          { from: "subtract_constant", to: "divide_coefficient", type: "enables", strength: 9 },
          { from: "divide_coefficient", to: "write_solution", type: "equals", strength: 10 },
          { from: "divide_coefficient", to: "write_solution", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_fund_02',
      problem: "Solve 3x - 2 ≤ 10",
      problemType: "inequalities_linear_programming",
      difficulty: "fundamental",
      description: "Basic linear inequality with less than or equal",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with linear expression",
            value: "3x - 2 ≤ 10",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "addition_step",
            name: "Addition Step",
            type: "values",
            description: "Add 2 to both sides",
            value: "3x ≤ 12",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by 3",
            value: "x ≤ 4",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x ≤ 4",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality to solve",
            value: "Step 1: 3x - 2 ≤ 10",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_add_constant",
            name: "Step 2: Add Constant",
            type: "steps",
            description: "Add 2 to both sides",
            value: "Step 2: 3x ≤ 12",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_divide_coefficient",
            name: "Step 3: Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by 3",
            value: "Step 3: x ≤ 4",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_solution",
            name: "Step 4: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 4: x ≤ 4",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "addition_step", type: "simplified by", strength: 10 },
          { from: "addition_step", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_add_constant", type: "enables", strength: 8 },
          { from: "step2_add_constant", to: "step3_divide_coefficient", type: "enables", strength: 9 },
          { from: "step3_divide_coefficient", to: "step4_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_fund_03',
      problem: "Solve -2x + 5 ≥ 1",
      problemType: "inequalities_linear_programming",
      difficulty: "fundamental",
      description: "Linear inequality with negative coefficient",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with negative coefficient",
            value: "-2x + 5 ≥ 1",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "subtraction_step",
            name: "Subtraction Step",
            type: "values",
            description: "Subtract 5 from both sides",
            value: "-2x ≥ -4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by -2 (reverse inequality)",
            value: "x ≤ 2",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x ≤ 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality to solve",
            value: "Step 1: -2x + 5 ≥ 1",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_subtract_constant",
            name: "Step 2: Subtract Constant",
            type: "steps",
            description: "Subtract 5 from both sides",
            value: "Step 2: -2x ≥ -4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_divide_negative",
            name: "Step 3: Divide by Negative",
            type: "steps",
            description: "Divide both sides by -2 (reverse inequality sign)",
            value: "Step 3: x ≤ 2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_solution",
            name: "Step 4: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 4: x ≤ 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "subtraction_step", type: "simplified by", strength: 10 },
          { from: "subtraction_step", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_subtract_constant", type: "enables", strength: 8 },
          { from: "step2_subtract_constant", to: "step3_divide_negative", type: "enables", strength: 9 },
          { from: "step3_divide_negative", to: "step4_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_fund_04',
      problem: "Solve 4x + 8 < 20",
      problemType: "inequalities_linear_programming",
      difficulty: "fundamental",
      description: "Basic linear inequality",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with linear expression",
            value: "4x + 8 < 20",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "subtraction_step",
            name: "Subtraction Step",
            type: "values",
            description: "Subtract 8 from both sides",
            value: "4x < 12",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by 4",
            value: "x < 3",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x < 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality to solve",
            value: "Step 1: 4x + 8 < 20",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_subtract_constant",
            name: "Step 2: Subtract Constant",
            type: "steps",
            description: "Subtract 8 from both sides",
            value: "Step 2: 4x < 12",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_divide_coefficient",
            name: "Step 3: Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by 4",
            value: "Step 3: x < 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_solution",
            name: "Step 4: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 4: x < 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "subtraction_step", type: "simplified by", strength: 10 },
          { from: "subtraction_step", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_subtract_constant", type: "enables", strength: 8 },
          { from: "step2_subtract_constant", to: "step3_divide_coefficient", type: "enables", strength: 9 },
          { from: "step3_divide_coefficient", to: "step4_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_fund_05',
      problem: "Solve 5x - 3 > 12",
      problemType: "inequalities_linear_programming",
      difficulty: "fundamental",
      description: "Basic linear inequality",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with linear expression",
            value: "5x - 3 > 12",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "addition_step",
            name: "Addition Step",
            type: "values",
            description: "Add 3 to both sides",
            value: "5x > 15",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by 5",
            value: "x > 3",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x > 3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality to solve",
            value: "Step 1: 5x - 3 > 12",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_add_constant",
            name: "Step 2: Add Constant",
            type: "steps",
            description: "Add 3 to both sides",
            value: "Step 2: 5x > 15",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_divide_coefficient",
            name: "Step 3: Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by 5",
            value: "Step 3: x > 3",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_solution",
            name: "Step 4: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 4: x > 3",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "addition_step", type: "simplified by", strength: 10 },
          { from: "addition_step", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_add_constant", type: "enables", strength: 8 },
          { from: "step2_add_constant", to: "step3_divide_coefficient", type: "enables", strength: 9 },
          { from: "step3_divide_coefficient", to: "step4_write_solution", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'ineq_linprog_inter_01',
      problem: "Solve 2x + 1 ≤ 3x - 4",
      problemType: "inequalities_linear_programming",
      difficulty: "intermediate",
      description: "Linear inequality with variable on both sides",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with variables on both sides",
            value: "2x + 1 ≤ 3x - 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "subtraction_step1",
            name: "Subtraction Step 1",
            type: "values",
            description: "Subtract 2x from both sides",
            value: "1 ≤ x - 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "addition_step",
            name: "Addition Step",
            type: "values",
            description: "Add 4 to both sides",
            value: "5 ≤ x",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "rewrite",
            name: "Rewrite",
            type: "values",
            description: "Rewrite in standard form",
            value: "x ≥ 5",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x ≥ 5",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality with variables on both sides",
            value: "Step 1: 2x + 1 ≤ 3x - 4",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_subtract_variable",
            name: "Step 2: Subtract Variable",
            type: "steps",
            description: "Subtract 2x from both sides to collect variables",
            value: "Step 2: 1 ≤ x - 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_add_constant",
            name: "Step 3: Add Constant",
            type: "steps",
            description: "Add 4 to both sides",
            value: "Step 3: 5 ≤ x",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_rewrite_standard",
            name: "Step 4: Rewrite in Standard Form",
            type: "steps",
            description: "Rewrite in standard form x ≥ 5",
            value: "Step 4: x ≥ 5",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_write_solution",
            name: "Step 5: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 5: x ≥ 5",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "subtraction_step1", type: "simplified by", strength: 10 },
          { from: "subtraction_step1", to: "addition_step", type: "simplified by", strength: 10 },
          { from: "addition_step", to: "rewrite", type: "rewritten as", strength: 10 },
          { from: "rewrite", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_subtract_variable", type: "enables", strength: 8 },
          { from: "step2_subtract_variable", to: "step3_add_constant", type: "enables", strength: 9 },
          { from: "step3_add_constant", to: "step4_rewrite_standard", type: "enables", strength: 9 },
          { from: "step4_rewrite_standard", to: "step5_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_inter_02',
      problem: "Solve 3(x - 2) > 2(x + 1)",
      problemType: "inequalities_linear_programming",
      difficulty: "intermediate",
      description: "Linear inequality with parentheses",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with parentheses",
            value: "3(x - 2) > 2(x + 1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "values",
            description: "Expand both sides",
            value: "3x - 6 > 2x + 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "subtraction_step",
            name: "Subtraction Step",
            type: "values",
            description: "Subtract 2x from both sides",
            value: "x - 6 > 2",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "addition_step",
            name: "Addition Step",
            type: "values",
            description: "Add 6 to both sides",
            value: "x > 8",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x > 8",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality with parentheses",
            value: "Step 1: 3(x - 2) > 2(x + 1)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_expand_both_sides",
            name: "Step 2: Expand Both Sides",
            type: "steps",
            description: "Expand both sides using distributive property",
            value: "Step 2: 3x - 6 > 2x + 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_subtract_variable",
            name: "Step 3: Subtract Variable",
            type: "steps",
            description: "Subtract 2x from both sides",
            value: "Step 3: x - 6 > 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_add_constant",
            name: "Step 4: Add Constant",
            type: "steps",
            description: "Add 6 to both sides",
            value: "Step 4: x > 8",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_write_solution",
            name: "Step 5: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 5: x > 8",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "expansion", type: "simplified by", strength: 10 },
          { from: "expansion", to: "subtraction_step", type: "simplified by", strength: 10 },
          { from: "subtraction_step", to: "addition_step", type: "simplified by", strength: 10 },
          { from: "addition_step", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_expand_both_sides", type: "enables", strength: 8 },
          { from: "step2_expand_both_sides", to: "step3_subtract_variable", type: "enables", strength: 9 },
          { from: "step3_subtract_variable", to: "step4_add_constant", type: "enables", strength: 9 },
          { from: "step4_add_constant", to: "step5_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_inter_03',
      problem: "Solve 2x + 3 < 4x - 1 and x > 0",
      problemType: "inequalities_linear_programming",
      difficulty: "intermediate",
      description: "Compound inequality",
      graphData: {
        nodes: [
          {
            id: "compound_inequality",
            name: "Compound Inequality",
            type: "concepts",
            description: "Two inequalities combined with AND",
            value: "2x + 3 < 4x - 1 and x > 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "first_inequality",
            name: "First Inequality",
            type: "values",
            description: "Solve 2x + 3 < 4x - 1",
            value: "2x + 3 < 4x - 1 → x > 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "second_inequality",
            name: "Second Inequality",
            type: "values",
            description: "The second condition",
            value: "x > 0",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "intersection",
            name: "Intersection",
            type: "values",
            description: "Find intersection of both conditions",
            value: "x > 2 (more restrictive condition)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x > 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify_compound",
            name: "Step 1: Identify Compound Inequality",
            type: "steps",
            description: "Recognize the compound inequality with AND",
            value: "Step 1: 2x + 3 < 4x - 1 and x > 0",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_solve_first_inequality",
            name: "Step 2: Solve First Inequality",
            type: "steps",
            description: "Solve 2x + 3 < 4x - 1",
            value: "Step 2: 2x + 3 < 4x - 1 → x > 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_identify_second_condition",
            name: "Step 3: Identify Second Condition",
            type: "steps",
            description: "Note the second condition x > 0",
            value: "Step 3: x > 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_intersection",
            name: "Step 4: Find Intersection",
            type: "steps",
            description: "Find the intersection of both conditions",
            value: "Step 4: x > 2 (more restrictive)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_write_solution",
            name: "Step 5: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 5: x > 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "compound_inequality", to: "first_inequality", type: "contains", strength: 9 },
          { from: "compound_inequality", to: "second_inequality", type: "contains", strength: 9 },
          { from: "first_inequality", to: "intersection", type: "combined with", strength: 10 },
          { from: "second_inequality", to: "intersection", type: "combined with", strength: 10 },
          { from: "intersection", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify_compound", to: "step2_solve_first_inequality", type: "enables", strength: 8 },
          { from: "step2_solve_first_inequality", to: "step3_identify_second_condition", type: "enables", strength: 9 },
          { from: "step3_identify_second_condition", to: "step4_find_intersection", type: "enables", strength: 9 },
          { from: "step4_find_intersection", to: "step5_write_solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_inter_04',
      problem: "Solve -3x + 6 ≥ 2x - 4",
      problemType: "inequalities_linear_programming",
      difficulty: "intermediate",
      description: "Linear inequality with negative coefficient",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with negative coefficient",
            value: "-3x + 6 ≥ 2x - 4",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "addition_step1",
            name: "Addition Step 1",
            type: "values",
            description: "Add 3x to both sides",
            value: "6 ≥ 5x - 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "addition_step2",
            name: "Addition Step 2",
            type: "values",
            description: "Add 4 to both sides",
            value: "10 ≥ 5x",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "division_step",
            name: "Division Step",
            type: "values",
            description: "Divide both sides by 5",
            value: "2 ≥ x",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Rewrite in standard form",
            value: "x ≤ 2",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality with negative coefficient",
            value: "Step 1: -3x + 6 ≥ 2x - 4",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_add_variable",
            name: "Step 2: Add Variable",
            type: "steps",
            description: "Add 3x to both sides to collect variables",
            value: "Step 2: 6 ≥ 5x - 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_add_constant",
            name: "Step 3: Add Constant",
            type: "steps",
            description: "Add 4 to both sides",
            value: "Step 3: 10 ≥ 5x",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_divide_coefficient",
            name: "Step 4: Divide by Coefficient",
            type: "steps",
            description: "Divide both sides by 5",
            value: "Step 4: 2 ≥ x",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_rewrite_standard",
            name: "Step 5: Rewrite in Standard Form",
            type: "steps",
            description: "Rewrite in standard form x ≤ 2",
            value: "Step 5: x ≤ 2",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "addition_step1", type: "simplified by", strength: 10 },
          { from: "addition_step1", to: "addition_step2", type: "simplified by", strength: 10 },
          { from: "addition_step2", to: "division_step", type: "simplified by", strength: 10 },
          { from: "division_step", to: "solution", type: "rewritten as", strength: 10 },
          { from: "step1_identify", to: "step2_add_variable", type: "enables", strength: 8 },
          { from: "step2_add_variable", to: "step3_add_constant", type: "enables", strength: 9 },
          { from: "step3_add_constant", to: "step4_divide_coefficient", type: "enables", strength: 9 },
          { from: "step4_divide_coefficient", to: "step5_rewrite_standard", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_inter_05',
      problem: "Solve 4(x + 2) ≤ 3(x + 3)",
      problemType: "inequalities_linear_programming",
      difficulty: "intermediate",
      description: "Linear inequality with parentheses",
      graphData: {
        nodes: [
          {
            id: "inequality",
            name: "Linear Inequality",
            type: "concepts",
            description: "An inequality with parentheses",
            value: "4(x + 2) ≤ 3(x + 3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "values",
            description: "Expand both sides",
            value: "4x + 8 ≤ 3x + 9",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "subtraction_step",
            name: "Subtraction Step",
            type: "values",
            description: "Subtract 3x from both sides",
            value: "x + 8 ≤ 9",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "subtraction_step2",
            name: "Subtraction Step 2",
            type: "values",
            description: "Subtract 8 from both sides",
            value: "x ≤ 1",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The solution set",
            value: "x ≤ 1",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the inequality with parentheses",
            value: "Step 1: 4(x + 2) ≤ 3(x + 3)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_expand_both_sides",
            name: "Step 2: Expand Both Sides",
            type: "steps",
            description: "Expand both sides using distributive property",
            value: "Step 2: 4x + 8 ≤ 3x + 9",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_subtract_variable",
            name: "Step 3: Subtract Variable",
            type: "steps",
            description: "Subtract 3x from both sides",
            value: "Step 3: x + 8 ≤ 9",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_subtract_constant",
            name: "Step 4: Subtract Constant",
            type: "steps",
            description: "Subtract 8 from both sides",
            value: "Step 4: x ≤ 1",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_write_solution",
            name: "Step 5: Write Solution",
            type: "steps",
            description: "State the final solution",
            value: "Step 5: x ≤ 1",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "inequality", to: "expansion", type: "simplified by", strength: 10 },
          { from: "expansion", to: "subtraction_step", type: "simplified by", strength: 10 },
          { from: "subtraction_step", to: "subtraction_step2", type: "simplified by", strength: 10 },
          { from: "subtraction_step2", to: "solution", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_expand_both_sides", type: "enables", strength: 8 },
          { from: "step2_expand_both_sides", to: "step3_subtract_variable", type: "enables", strength: 9 },
          { from: "step3_subtract_variable", to: "step4_subtract_constant", type: "enables", strength: 9 },
          { from: "step4_subtract_constant", to: "step5_write_solution", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'ineq_linprog_hard_01',
      problem: "Maximize Z = 3x + 2y subject to x + y ≤ 4, 2x + y ≤ 6, x ≥ 0, y ≥ 0",
      problemType: "inequalities_linear_programming",
      difficulty: "harder",
      description: "Basic linear programming problem",
      graphData: {
        nodes: [
          {
            id: "objective_function",
            name: "Objective Function",
            type: "concepts",
            description: "The function to maximize",
            value: "Z = 3x + 2y",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "constraint1",
            name: "Constraint 1",
            type: "concepts",
            description: "First constraint",
            value: "x + y ≤ 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "constraint2",
            name: "Constraint 2",
            type: "concepts",
            description: "Second constraint",
            value: "2x + y ≤ 6",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "non_negativity",
            name: "Non-negativity",
            type: "concepts",
            description: "Non-negative variables",
            value: "x ≥ 0, y ≥ 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "feasible_region",
            name: "Feasible Region",
            type: "concepts",
            description: "Region satisfying all constraints",
            value: "Polygon with vertices",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "optimal_solution",
            name: "Optimal Solution",
            type: "values",
            description: "Maximum value at corner point",
            value: "Z = 12 at (2, 2)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify_objective",
            name: "Step 1: Identify Objective",
            type: "steps",
            description: "Recognize the objective function to maximize",
            value: "Step 1: Maximize Z = 3x + 2y",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_list_constraints",
            name: "Step 2: List Constraints",
            type: "steps",
            description: "List all constraints",
            value: "Step 2: x + y ≤ 4, 2x + y ≤ 6, x ≥ 0, y ≥ 0",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_graph_feasible_region",
            name: "Step 3: Graph Feasible Region",
            type: "steps",
            description: "Graph the feasible region",
            value: "Step 3: Plot constraints and find intersection",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_corner_points",
            name: "Step 4: Find Corner Points",
            type: "steps",
            description: "Find corner points of feasible region",
            value: "Step 4: (0,0), (0,4), (2,2), (3,0)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_evaluate_objective",
            name: "Step 5: Evaluate Objective",
            type: "steps",
            description: "Evaluate objective function at corner points",
            value: "Step 5: Z = 12 at (2,2) is maximum",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "objective_function", to: "constraint1", type: "subject to", strength: 9 },
          { from: "objective_function", to: "constraint2", type: "subject to", strength: 9 },
          { from: "objective_function", to: "non_negativity", type: "subject to", strength: 9 },
          { from: "constraint1", to: "feasible_region", type: "defines", strength: 10 },
          { from: "constraint2", to: "feasible_region", type: "defines", strength: 10 },
          { from: "feasible_region", to: "optimal_solution", type: "gives", strength: 10 },
          { from: "step1_identify_objective", to: "step2_list_constraints", type: "enables", strength: 8 },
          { from: "step2_list_constraints", to: "step3_graph_feasible_region", type: "enables", strength: 9 },
          { from: "step3_graph_feasible_region", to: "step4_find_corner_points", type: "enables", strength: 9 },
          { from: "step4_find_corner_points", to: "step5_evaluate_objective", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_hard_02',
      problem: "Solve the system: 2x + y ≥ 4, x + 2y ≥ 4, x ≥ 0, y ≥ 0",
      problemType: "inequalities_linear_programming",
      difficulty: "harder",
      description: "System of linear inequalities",
      graphData: {
        nodes: [
          {
            id: "system",
            name: "System of Inequalities",
            type: "concepts",
            description: "Multiple inequalities to solve together",
            value: "2x + y ≥ 4, x + 2y ≥ 4, x ≥ 0, y ≥ 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "constraint1",
            name: "Constraint 1",
            type: "concepts",
            description: "First inequality",
            value: "2x + y ≥ 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "constraint2",
            name: "Constraint 2",
            type: "concepts",
            description: "Second inequality",
            value: "x + 2y ≥ 4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "non_negativity",
            name: "Non-negativity",
            type: "concepts",
            description: "Non-negative variables",
            value: "x ≥ 0, y ≥ 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "feasible_region",
            name: "Feasible Region",
            type: "values",
            description: "Region satisfying all inequalities",
            value: "Unbounded region in first quadrant",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify_system",
            name: "Step 1: Identify System",
            type: "steps",
            description: "Recognize the system of inequalities",
            value: "Step 1: 2x + y ≥ 4, x + 2y ≥ 4, x ≥ 0, y ≥ 0",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_graph_constraints",
            name: "Step 2: Graph Constraints",
            type: "steps",
            description: "Graph each inequality",
            value: "Step 2: Plot 2x + y ≥ 4, x + 2y ≥ 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_apply_non_negativity",
            name: "Step 3: Apply Non-negativity",
            type: "steps",
            description: "Apply x ≥ 0, y ≥ 0 constraints",
            value: "Step 3: Restrict to first quadrant",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_intersection",
            name: "Step 4: Find Intersection",
            type: "steps",
            description: "Find intersection of all constraints",
            value: "Step 4: Unbounded region in first quadrant",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_describe_solution",
            name: "Step 5: Describe Solution",
            type: "steps",
            description: "Describe the feasible region",
            value: "Step 5: All points in first quadrant satisfying both inequalities",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "system", to: "constraint1", type: "contains", strength: 9 },
          { from: "system", to: "constraint2", type: "contains", strength: 9 },
          { from: "system", to: "non_negativity", type: "contains", strength: 9 },
          { from: "constraint1", to: "feasible_region", type: "defines", strength: 10 },
          { from: "constraint2", to: "feasible_region", type: "defines", strength: 10 },
          { from: "non_negativity", to: "feasible_region", type: "defines", strength: 10 },
          { from: "step1_identify_system", to: "step2_graph_constraints", type: "enables", strength: 8 },
          { from: "step2_graph_constraints", to: "step3_apply_non_negativity", type: "enables", strength: 9 },
          { from: "step3_apply_non_negativity", to: "step4_find_intersection", type: "enables", strength: 9 },
          { from: "step4_find_intersection", to: "step5_describe_solution", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_hard_03',
      problem: "Minimize C = 2x + 3y subject to x + y ≥ 3, x + 2y ≥ 4, x ≥ 0, y ≥ 0",
      problemType: "inequalities_linear_programming",
      difficulty: "harder",
      description: "Linear programming minimization",
      graphData: {
        nodes: [
          {
            id: "objective_function",
            name: "Objective Function",
            type: "concepts",
            description: "The function to minimize",
            value: "C = 2x + 3y",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "constraint1",
            name: "Constraint 1",
            type: "concepts",
            description: "First constraint",
            value: "x + y ≥ 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "constraint2",
            name: "Constraint 2",
            type: "concepts",
            description: "Second constraint",
            value: "x + 2y ≥ 4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "non_negativity",
            name: "Non-negativity",
            type: "concepts",
            description: "Non-negative variables",
            value: "x ≥ 0, y ≥ 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "feasible_region",
            name: "Feasible Region",
            type: "concepts",
            description: "Region satisfying all constraints",
            value: "Unbounded region",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "optimal_solution",
            name: "Optimal Solution",
            type: "values",
            description: "Minimum value at corner point",
            value: "C = 8 at (2, 1)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify_objective",
            name: "Step 1: Identify Objective",
            type: "steps",
            description: "Recognize the objective function to minimize",
            value: "Step 1: Minimize C = 2x + 3y",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_list_constraints",
            name: "Step 2: List Constraints",
            type: "steps",
            description: "List all constraints",
            value: "Step 2: x + y ≥ 3, x + 2y ≥ 4, x ≥ 0, y ≥ 0",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_graph_feasible_region",
            name: "Step 3: Graph Feasible Region",
            type: "steps",
            description: "Graph the feasible region",
            value: "Step 3: Plot constraints and find intersection",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_corner_points",
            name: "Step 4: Find Corner Points",
            type: "steps",
            description: "Find corner points of feasible region",
            value: "Step 4: (0,3), (2,1), (4,0)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_evaluate_objective",
            name: "Step 5: Evaluate Objective",
            type: "steps",
            description: "Evaluate objective function at corner points",
            value: "Step 5: C = 8 at (2,1) is minimum",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "objective_function", to: "constraint1", type: "subject to", strength: 9 },
          { from: "objective_function", to: "constraint2", type: "subject to", strength: 9 },
          { from: "objective_function", to: "non_negativity", type: "subject to", strength: 9 },
          { from: "constraint1", to: "feasible_region", type: "defines", strength: 10 },
          { from: "constraint2", to: "feasible_region", type: "defines", strength: 10 },
          { from: "feasible_region", to: "optimal_solution", type: "gives", strength: 10 },
          { from: "step1_identify_objective", to: "step2_list_constraints", type: "enables", strength: 8 },
          { from: "step2_list_constraints", to: "step3_graph_feasible_region", type: "enables", strength: 9 },
          { from: "step3_graph_feasible_region", to: "step4_find_corner_points", type: "enables", strength: 9 },
          { from: "step4_find_corner_points", to: "step5_evaluate_objective", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_hard_04',
      problem: "Solve 3x + 2y ≤ 6, 2x + 3y ≤ 6, x ≥ 0, y ≥ 0",
      problemType: "inequalities_linear_programming",
      difficulty: "harder",
      description: "System of linear inequalities with bounded region",
      graphData: {
        nodes: [
          {
            id: "system",
            name: "System of Inequalities",
            type: "concepts",
            description: "Multiple inequalities to solve together",
            value: "3x + 2y ≤ 6, 2x + 3y ≤ 6, x ≥ 0, y ≥ 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "constraint1",
            name: "Constraint 1",
            type: "concepts",
            description: "First inequality",
            value: "3x + 2y ≤ 6",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "constraint2",
            name: "Constraint 2",
            type: "concepts",
            description: "Second inequality",
            value: "2x + 3y ≤ 6",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "non_negativity",
            name: "Non-negativity",
            type: "concepts",
            description: "Non-negative variables",
            value: "x ≥ 0, y ≥ 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "feasible_region",
            name: "Feasible Region",
            type: "values",
            description: "Region satisfying all inequalities",
            value: "Bounded polygon in first quadrant",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "step1_identify_system",
            name: "Step 1: Identify System",
            type: "steps",
            description: "Recognize the system of inequalities",
            value: "Step 1: 3x + 2y ≤ 6, 2x + 3y ≤ 6, x ≥ 0, y ≥ 0",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_graph_constraints",
            name: "Step 2: Graph Constraints",
            type: "steps",
            description: "Graph each inequality",
            value: "Step 2: Plot 3x + 2y ≤ 6, 2x + 3y ≤ 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_apply_non_negativity",
            name: "Step 3: Apply Non-negativity",
            type: "steps",
            description: "Apply x ≥ 0, y ≥ 0 constraints",
            value: "Step 3: Restrict to first quadrant",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_intersection",
            name: "Step 4: Find Intersection",
            type: "steps",
            description: "Find intersection of all constraints",
            value: "Step 4: Bounded polygon in first quadrant",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_describe_solution",
            name: "Step 5: Describe Solution",
            type: "steps",
            description: "Describe the feasible region",
            value: "Step 5: Bounded region with vertices (0,0), (2,0), (1.2,1.2), (0,2)",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "system", to: "constraint1", type: "contains", strength: 9 },
          { from: "system", to: "constraint2", type: "contains", strength: 9 },
          { from: "system", to: "non_negativity", type: "contains", strength: 9 },
          { from: "constraint1", to: "feasible_region", type: "defines", strength: 10 },
          { from: "constraint2", to: "feasible_region", type: "defines", strength: 10 },
          { from: "non_negativity", to: "feasible_region", type: "defines", strength: 10 },
          { from: "step1_identify_system", to: "step2_graph_constraints", type: "enables", strength: 8 },
          { from: "step2_graph_constraints", to: "step3_apply_non_negativity", type: "enables", strength: 9 },
          { from: "step3_apply_non_negativity", to: "step4_find_intersection", type: "enables", strength: 9 },
          { from: "step4_find_intersection", to: "step5_describe_solution", type: "enables", strength: 10 }
        ]
      }
    },
    {
      id: 'ineq_linprog_hard_05',
      problem: "Maximize P = 4x + 3y subject to x + y ≤ 5, 2x + y ≤ 8, x ≥ 0, y ≥ 0",
      problemType: "inequalities_linear_programming",
      difficulty: "harder",
      description: "Linear programming maximization",
      graphData: {
        nodes: [
          {
            id: "objective_function",
            name: "Objective Function",
            type: "concepts",
            description: "The function to maximize",
            value: "P = 4x + 3y",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "constraint1",
            name: "Constraint 1",
            type: "concepts",
            description: "First constraint",
            value: "x + y ≤ 5",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "constraint2",
            name: "Constraint 2",
            type: "concepts",
            description: "Second constraint",
            value: "2x + y ≤ 8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "non_negativity",
            name: "Non-negativity",
            type: "concepts",
            description: "Non-negative variables",
            value: "x ≥ 0, y ≥ 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "feasible_region",
            name: "Feasible Region",
            type: "concepts",
            description: "Region satisfying all constraints",
            value: "Bounded polygon",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "optimal_solution",
            name: "Optimal Solution",
            type: "values",
            description: "Maximum value at corner point",
            value: "P = 20 at (4, 0)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify_objective",
            name: "Step 1: Identify Objective",
            type: "steps",
            description: "Recognize the objective function to maximize",
            value: "Step 1: Maximize P = 4x + 3y",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_list_constraints",
            name: "Step 2: List Constraints",
            type: "steps",
            description: "List all constraints",
            value: "Step 2: x + y ≤ 5, 2x + y ≤ 8, x ≥ 0, y ≥ 0",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_graph_feasible_region",
            name: "Step 3: Graph Feasible Region",
            type: "steps",
            description: "Graph the feasible region",
            value: "Step 3: Plot constraints and find intersection",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step4_find_corner_points",
            name: "Step 4: Find Corner Points",
            type: "steps",
            description: "Find corner points of feasible region",
            value: "Step 4: (0,0), (0,5), (3,2), (4,0)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step5_evaluate_objective",
            name: "Step 5: Evaluate Objective",
            type: "steps",
            description: "Evaluate objective function at corner points",
            value: "Step 5: P = 20 at (4,0) is maximum",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "objective_function", to: "constraint1", type: "subject to", strength: 9 },
          { from: "objective_function", to: "constraint2", type: "subject to", strength: 9 },
          { from: "objective_function", to: "non_negativity", type: "subject to", strength: 9 },
          { from: "constraint1", to: "feasible_region", type: "defines", strength: 10 },
          { from: "constraint2", to: "feasible_region", type: "defines", strength: 10 },
          { from: "feasible_region", to: "optimal_solution", type: "gives", strength: 10 },
          { from: "step1_identify_objective", to: "step2_list_constraints", type: "enables", strength: 8 },
          { from: "step2_list_constraints", to: "step3_graph_feasible_region", type: "enables", strength: 9 },
          { from: "step3_graph_feasible_region", to: "step4_find_corner_points", type: "enables", strength: 9 },
          { from: "step4_find_corner_points", to: "step5_evaluate_objective", type: "enables", strength: 10 }
        ]
      }
    }
  ]
}; 